import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { pollMonitoring } from '../orchestration-polling.util';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { MsfPChartComponent } from '@components/msf-p-chart/msf-p-chart.component';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { OrchestrationDatabaseSlotBoardComponent } from '@app/pages/admin/components/orchestration-database-slot-board/orchestration-database-slot-board';
import { OrchestrationObjectStorageBoardComponent } from '@app/pages/admin/components/orchestration-object-storage-board/orchestration-object-storage-board';
import { OrchestrationStorageOccupancyComponent } from '@app/pages/admin/components/orchestration-storage-occupancy/orchestration-storage-occupancy';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { OperationMonitoringService as OperationMonitoringApiService } from '@6grain/masofa-monolith-api-client/common/api/operationMonitoring.service';
import { DatabaseConnectionsSnapshot } from '@6grain/masofa-monolith-api-client/common/model/databaseConnectionsSnapshot';
import {
  DATABASE_CONNECTION_GROUPS_PAGE_SIZE,
  databaseConnectionGroupSearchFields,
  databaseSizePairSearchFields,
  flattenDatabaseSizePairs,
  formatDatabaseSizeBytes,
  groupDatabaseSizeRows,
  readDatabaseSizeRows,
  readDatabaseTables,
  readDiskVolume,
  readTotalDatabaseSizeBytes,
} from '@app/pages/admin/orchestration-database-connections.util';
import {
  ObjectStorageSnapshot,
  ObjectStorageWriteSeries,
  ObjectStorageWriteWindow,
  buildObjectStorageWriteChart,
  objectStorageBucketSearchFields,
  objectStorageWriteChartConfig,
  objectStorageWriteWindowBounds,
  parseObjectStorageWriteWindow,
} from '@app/pages/admin/orchestration-object-storage.util';
import { occupancyWithVolume } from '@app/pages/admin/orchestration-storage-occupancy.util';

@Component({
  selector: 'app-orchestration-databases-page',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SecondaryButton,
    DataTableComponent,
    MsfPChartComponent,
    OrchestrationTableHeadingComponent,
    OrchestrationDatabaseSlotBoardComponent,
    OrchestrationObjectStorageBoardComponent,
    OrchestrationStorageOccupancyComponent,
    CopyableIdentifierComponent,
  ],
  templateUrl: './orchestration-databases-page.html',
  styleUrl: './orchestration-databases-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationDatabasesPageComponent implements OnInit, OnDestroy {
  private readonly monitoring = inject(OperationMonitoringApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  private readonly writersTemplate = viewChild<TemplateRef<unknown>>('writersTemplate');

  readonly loading = signal(true);
  readonly storageLoading = signal(true);
  readonly errorMessage = signal('');
  readonly storageErrorMessage = signal('');
  readonly writesErrorMessage = signal('');
  readonly snapshot = signal<DatabaseConnectionsSnapshot | null>(null);
  readonly storageSnapshot = signal<ObjectStorageSnapshot | null>(null);
  readonly writeSeries = signal<ObjectStorageWriteSeries | null>(null);
  readonly databaseSearch = signal('');
  readonly bucketSearch = signal('');
  readonly groupSearch = signal('');
  readonly storageWindow = signal<ObjectStorageWriteWindow>('24h');
  readonly writeWindowChips = [
    { id: '24h' as const, label: '24 hours' },
    { id: '7d' as const, label: '7 days' },
  ];
  readonly writeChartConfig = objectStorageWriteChartConfig();

  readonly visibleGroups = computed(() => filterByCatalogQuery(
    this.snapshot()?.groups ?? [],
    this.groupSearch(),
    databaseConnectionGroupSearchFields,
  ));

  readonly visibleDatabasePairs = computed(() => filterByCatalogQuery(
    groupDatabaseSizeRows(readDatabaseSizeRows(this.snapshot())),
    this.databaseSearch(),
    databaseSizePairSearchFields,
  ));

  readonly visibleDatabases = computed(() => flattenDatabaseSizePairs(this.visibleDatabasePairs()).map(row => ({
    name: row.name ?? '',
    sizeBytes: row.sizeBytes ?? 0,
    sizeLabel: formatDatabaseSizeBytes(row.sizeBytes),
    tables: readDatabaseTables(row),
  })));

  readonly totalSizeLabel = computed(() => formatDatabaseSizeBytes(readTotalDatabaseSizeBytes(this.snapshot())));

  readonly databaseOccupancy = computed(() => occupancyWithVolume(
    this.visibleDatabasePairs().map(pair => ({
      id: pair.id,
      label: pair.label,
      sizeBytes: pair.sizeBytes,
      hint: pair.members.length === 1 && pair.members[0].hint === 'History'
        ? pair.members[0].hint
        : undefined,
      children: pair.members.length > 1
        ? pair.members.map(member => ({
          id: member.name,
          label: member.name,
          sizeBytes: member.sizeBytes,
          hint: member.hint,
          children: member.tables.map(table => ({
            id: `${member.name}.${table.schema}.${table.name}`,
            label: `${table.schema}.${table.name}`,
            sizeBytes: table.sizeBytes ?? 0,
          })),
        }))
        : pair.members[0]?.tables.map(table => ({
          id: `${pair.members[0].name}.${table.schema}.${table.name}`,
          label: `${table.schema}.${table.name}`,
          sizeBytes: table.sizeBytes ?? 0,
        })) ?? [],
    })),
    readDiskVolume(this.snapshot()),
  ));

  readonly bucketOccupancy = computed(() => {
    const rows = this.visibleBuckets();
    return occupancyWithVolume(
      rows.map(row => ({
        id: row.name,
        label: row.name,
        sizeBytes: row.sizeBytes,
      })),
      readDiskVolume(this.storageSnapshot()),
    );
  });

  readonly visibleBuckets = computed(() => filterByCatalogQuery(
    this.storageSnapshot()?.buckets ?? [],
    this.bucketSearch(),
    objectStorageBucketSearchFields,
  ).map(row => ({
    name: row.name ?? '',
    sizeBytes: row.sizeBytes ?? 0,
    sizeLabel: formatDatabaseSizeBytes(row.sizeBytes),
    objectCount: row.objectCount ?? 0,
    lastWriteAt: row.lastWriteAt ?? null,
    writers: uniqueWriters(row.writers ?? []),
  })));

  readonly writeChartData = computed(() => buildObjectStorageWriteChart(
    this.writeSeries(),
    this.visibleBuckets().map(row => row.name),
  ));

  readonly sizeTableColumns: TableColumn[] = [
    { field: 'name', header: 'Database', type: 'text' },
    { field: 'sizeLabel', header: 'Size', type: 'text' },
  ];

  readonly sizeTableConfig: TableConfig = {
    paginator: true,
    rows: DATABASE_CONNECTION_GROUPS_PAGE_SIZE,
    keepOrder: true,
    emptyMessage: 'No database sizes',
  };

  readonly bucketTableColumns = computed<TableColumn[]>(() => [
    { field: 'name', header: 'Bucket', type: 'text' },
    { field: 'sizeLabel', header: 'Size', type: 'text' },
    { field: 'objectCount', header: 'Objects', type: 'number' },
    { field: 'lastWriteAt', header: 'Last write', type: 'date' },
    {
      field: 'writers',
      header: 'Writers',
      type: 'custom',
      customTemplate: this.writersTemplate(),
    },
  ]);

  readonly bucketTableConfig: TableConfig = {
    paginator: true,
    rows: DATABASE_CONNECTION_GROUPS_PAGE_SIZE,
    keepOrder: true,
    emptyMessage: 'No object-storage buckets',
  };

  readonly tableColumns: TableColumn[] = [
    { field: 'databaseName', header: 'Database', type: 'text' },
    { field: 'userName', header: 'User', type: 'text' },
    { field: 'applicationName', header: 'Application', type: 'text' },
    { field: 'clientAddress', header: 'Client', type: 'text' },
    { field: 'state', header: 'State', type: 'text' },
    { field: 'waitEvent', header: 'Wait event', type: 'text' },
    { field: 'connectionCount', header: 'Connections', type: 'number' },
  ];

  readonly tableConfig: TableConfig = {
    paginator: true,
    rows: DATABASE_CONNECTION_GROUPS_PAGE_SIZE,
    keepOrder: true,
    emptyMessage: 'No connection groups',
  };

  ngOnInit(): void {
    pollMonitoring(() => this.monitoring.monitoringDatabaseConnectionsGet(), this.refresh$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(connections => {
        if (connections) {
          this.snapshot.set(connections);
          this.errorMessage.set('');
        } else if (!this.snapshot()) {
          this.errorMessage.set('Failed to load database connections');
        }
        this.loading.set(false);
      });
    pollMonitoring(() => this.monitoring.monitoringObjectStorageGet(), this.refresh$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(storage => {
        if (storage) {
          this.storageSnapshot.set(storage);
          this.storageErrorMessage.set('');
        } else if (!this.storageSnapshot()) {
          this.storageErrorMessage.set('Failed to load object storage');
        }
        this.storageLoading.set(false);
      });
    this.route.queryParamMap.pipe(
      map(params => parseObjectStorageWriteWindow(params.get('storageWindow'))),
      distinctUntilChanged(),
      tap(window => {
        this.storageWindow.set(window);
        this.writeSeries.set(null);
        this.writesErrorMessage.set('');
      }),
      switchMap(window => pollMonitoring(() => {
        const bounds = objectStorageWriteWindowBounds(window);
        return this.monitoring.monitoringObjectStorageWritesGet(bounds.from, bounds.to);
      }, this.refresh$)),
      takeUntil(this.destroy$),
    ).subscribe(writes => {
      if (writes) {
        this.writeSeries.set(writes);
        this.writesErrorMessage.set('');
      } else if (!this.writeSeries()) {
        this.writesErrorMessage.set('Failed to load object storage');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.refresh$.complete();
  }

  refresh(): void {
    this.refresh$.next();
  }

  selectWriteWindow(window: ObjectStorageWriteWindow): void {
    if (window === this.storageWindow()) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { storageWindow: window === '24h' ? null : window },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

function uniqueWriters(
  writers: Array<{ processKey?: string | null; displayName?: string | null; command?: string | null }>,
): Array<{ processKey: string; displayName: string; command: string }> {
  const seen = new Set<string>();
  const rows: Array<{ processKey: string; displayName: string; command: string }> = [];
  for (const writer of writers) {
    const processKey = writer.processKey?.trim() ?? '';
    if (!processKey || seen.has(processKey)) {
      continue;
    }
    seen.add(processKey);
    rows.push({
      processKey,
      displayName: writer.displayName?.trim() || processKey,
      command: writer.command ?? '',
    });
  }
  return rows;
}
